// CoursePlayerPage.jsx — full-bleed player: top breadcrumb + sidebar + content + footer.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MessengerLayout from "../../layout/MessengerLayout";
import {
  ChevronLeft, Check, CheckCircle2, Circle, Play, FileText, Link as LinkIcon,
  Award, RotateCcw, ExternalLink,
} from "lucide-react";
import { toast } from "../../components/ui/Toast";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const NEON  = "#00FF99";
const AMBER = "#EF9F27";
const RED   = "#E24B4A";
const BLUE  = "#378ADD";

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const isPlaceholderUrl = (u) => !u || /example\.com/i.test(u);
const absoluteUrl     = (u) => (u && u.startsWith("/") ? `${API_BASE}${u}` : u);

export default function CoursePlayerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [done, setDone] = useState({});  // content_item_id -> true
  const [currentIdx, setCurrentIdx] = useState(0);
  const [quizMode, setQuizMode] = useState(false);
  const [result, setResult] = useState(null);

  // Video tracking (lifted to parent so it survives re-renders cleanly)
  const videoRef         = useRef(null);
  const autoCompleteRef  = useRef(false);
  const [watchedPct, setWatchedPct] = useState(0);

  const load = useCallback(async () => {
    const r = await fetch(`${API_BASE}/api/learning/courses/${id}`, { headers: authHeaders() });
    if (r.ok) setCourse(await r.json());
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // Reset video tracking when navigating between modules
  useEffect(() => {
    autoCompleteRef.current = false;
    setWatchedPct(0);
  }, [currentIdx]);

  if (!course) {
    return (
      <MessengerLayout>
        <p style={{ color: "#666" }}>Loading…</p>
      </MessengerLayout>
    );
  }

  const items = course.content || [];
  const itemsDone = items.filter((it) => done[it.id]).length;
  const allDone   = items.length > 0 && itemsDone === items.length;
  const overallPct = items.length === 0 ? 100 : Math.round(itemsDone / items.length * 100);
  const current = items[currentIdx];
  const enrolmentId = course.enrolment_id;

  // Common progress-update helper — handles backend auto-complete signals.
  const sendProgress = async (body) => {
    if (!enrolmentId) return null;
    try {
      const r = await fetch(
        `${API_BASE}/api/learning/enrolments/${enrolmentId}/progress`,
        {
          method: "PATCH",
          headers: { ...authHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );
      if (!r.ok) return null;
      const data = await r.json();
      if (data.auto_completed) {
        if (data.requires_quiz) {
          setQuizMode(true);
        } else {
          setResult({
            passed: true,
            auto_completed: true,
            certificate_id: data.certificate_id,
            score: 100,
            feedback: [],
            pass_score: 100,
          });
        }
      }
      return data;
    } catch (e) {
      console.error("Progress error:", e);
      return null;
    }
  };

  const markDone = async () => {
    if (!current) return;
    const next = { ...done, [current.id]: true };
    setDone(next);
    const newPct = Math.round(Object.keys(next).length / items.length * 100);
    const isLastItem = Object.keys(next).length === items.length;
    const result = await sendProgress({
      progress_pct:    isLastItem ? 100 : newPct,
      content_item_id: current.id,
      action:          isLastItem ? "completed" : undefined,
    });
    if (result === null) {
      toast.error("Could not mark as complete");
    } else {
      toast.success("Marked complete");
    }
    if (currentIdx < items.length - 1) setCurrentIdx(currentIdx + 1);
  };

  const isFirst = currentIdx === 0;
  const isLast  = currentIdx === items.length - 1;
  const prev    = () => { if (!isFirst) setCurrentIdx(currentIdx - 1); };
  const next    = () => { if (!isLast)  setCurrentIdx(currentIdx + 1); };

  // Lesson breadcrumb label
  const lessonLabel = quizMode
    ? "Assessment"
    : (result ? "Result" : (current?.title || ""));

  return (
    <MessengerLayout>
      <div
        className="text-white"
        style={{
          // Break out of MessengerLayout's `<main className="w-full px-6 py-6 pl-20">`
          // padding so the player can fill the entire visible area edge-to-edge.
          margin: "-24px -24px -24px -80px",
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 56px)",  // 56px = TopToolbar
          background: "#080808",
          overflow: "hidden",
        }}
      >
        {/* ── Top breadcrumb bar ── */}
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "12px 20px 12px 80px",  // left pad clears the FAB stack
          borderBottom: "0.5px solid #1a1a1a",
          background: "#0a0a0a",
          flexShrink: 0,
        }}>
          <button
            onClick={() => navigate("/messenger/learning")}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              fontSize: 13, color: "#888",
              background: "none", border: "none", cursor: "pointer", padding: 0,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#ddd")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#888")}
          >
            <ChevronLeft size={14} /> Back
          </button>
          <span style={{ fontSize: 13, color: "#888" }}>{course.title}</span>
          {lessonLabel && (
            <>
              <span style={{ color: "#333" }}>›</span>
              <span style={{ fontSize: 13, color: "#ccc" }}>{lessonLabel}</span>
            </>
          )}
        </div>

        {/* ── Body grid: sidebar + main ── */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "280px 1fr",
          flex: 1,
          overflow: "hidden",
          minHeight: 0,
        }}>
          {/* ── Sidebar ── */}
          <aside style={{
            background: "#0a0a0a",
            borderRight: "0.5px solid #1a1a1a",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            padding: "20px 0",
            height: "100%",
          }}>
            {/* Sidebar header */}
            <div style={{
              padding: "0 16px 16px 16px",
              borderBottom: "0.5px solid #1a1a1a",
              marginBottom: 8,
            }}>
              <h2 style={{ fontSize: 14, color: "#fff", fontWeight: 500, margin: "0 0 8px 0" }}>
                {course.title}
              </h2>
              <ProgressBar value={overallPct} />
              <div style={{ fontSize: 10, color: "#666", marginTop: 6 }}>
                {itemsDone}/{items.length} done · {overallPct}%
              </div>
            </div>

            {/* Module list */}
            {items.map((it, i) => {
              const active = currentIdx === i && !quizMode && !result;
              const isComplete = !!done[it.id];
              return (
                <button
                  key={it.id}
                  onClick={() => { setCurrentIdx(i); setQuizMode(false); setResult(null); }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 16px",
                    cursor: "pointer",
                    fontSize: 13,
                    color: active ? NEON : (isComplete ? "#555" : "#888"),
                    background: active ? "rgba(0,255,153,0.06)" : "transparent",
                    borderLeft: `2px solid ${active ? NEON : "transparent"}`,
                    border: "none",
                    borderRadius: 0,
                    textAlign: "left",
                    width: "100%",
                    whiteSpace: "normal",
                  }}
                >
                  {isComplete
                    ? <CheckCircle2 size={14} color={NEON} />
                    : <Circle size={14} color="#444" />}
                  <span style={{ flex: 1 }}>{it.title}</span>
                  <span style={{ fontSize: 10, color: "#444" }}>{it.duration_minutes}m</span>
                </button>
              );
            })}

            {/* Assessment entry */}
            <button
              onClick={() => { setQuizMode(true); setResult(null); }}
              disabled={!allDone}
              style={{
                marginTop: 10,
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 16px",
                cursor: allDone ? "pointer" : "not-allowed",
                fontSize: 13,
                background: quizMode || result ? "rgba(239,159,39,0.06)" : "transparent",
                borderLeft: `2px solid ${(quizMode || result) ? AMBER : "transparent"}`,
                border: "none",
                color: allDone ? AMBER : "#444",
                textAlign: "left",
                width: "100%",
              }}
            >
              <Award size={14} color={allDone ? AMBER : "#444"} />
              <span style={{ flex: 1 }}>Assessment</span>
              {!allDone && <span style={{ fontSize: 10, color: "#444" }}>locked</span>}
            </button>
          </aside>

          {/* ── Main content panel ── */}
          <section style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            background: "#080808",
            minWidth: 0,
          }}>
            {/* Content header — lesson title */}
            <div style={{
              padding: "20px 28px 16px 28px",
              borderBottom: "0.5px solid #1a1a1a",
              fontSize: 18,
              fontWeight: 500,
              color: "#fff",
              flexShrink: 0,
            }}>
              {quizMode && !result ? "Assessment"
                : result ? "Your result"
                : (current?.title || "—")}
            </div>

            {/* Content body — scrollable */}
            <div style={{
              flex: 1,
              overflowY: "auto",
              padding: "24px 28px",
              minHeight: 0,
            }}>
              {!quizMode && !result && current && (
                <ContentView
                  item={current}
                  videoRef={videoRef}
                  watchedPct={watchedPct}
                  setWatchedPct={setWatchedPct}
                  autoCompleteRef={autoCompleteRef}
                  sendProgress={sendProgress}
                />
              )}
              {quizMode && !result && (
                <QuizRunner course={course} onResult={setResult} />
              )}
              {result && (
                <QuizResult
                  course={course}
                  result={result}
                  onRetry={() => setResult(null)}
                  onBack={() => navigate("/messenger/learning")}
                />
              )}
            </div>

            {/* Content footer — nav buttons (only in lesson mode) */}
            {!quizMode && !result && current && (
              <div style={{
                padding: "16px 28px",
                borderTop: "0.5px solid #1a1a1a",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexShrink: 0,
                background: "#0a0a0a",
                gap: 8,
              }}>
                <button onClick={prev} disabled={isFirst}
                  style={{ ...btnOutline, opacity: isFirst ? 0.4 : 1 }}>
                  ← Previous
                </button>

                {/* Video items: gate "Mark complete" until 90% watched. */}
                {current.content_type === "video" && watchedPct < 90 ? (
                  <span style={{ fontSize: 12, color: "#333" }}>
                    Watch to 90% to complete
                  </span>
                ) : (
                  <button onClick={markDone} style={btnPrimary}>
                    <Check size={12} /> {done[current.id] ? "Re-mark complete" : "Mark complete"}
                  </button>
                )}

                <button onClick={next} disabled={isLast}
                  style={{ ...btnOutline, opacity: isLast ? 0.4 : 1 }}>
                  Next →
                </button>
              </div>
            )}
          </section>
        </div>
      </div>
    </MessengerLayout>
  );
}

// ── ContentView — renders just the lesson body. Per-type behaviour:
//    link     → preview card; "Open →" opens the URL + sends progress 50%
//    video    → <video controls>; auto-progress + auto-complete at 90%
//    document → PDF in iframe; non-PDF as Download card
//    text     → plain markdown-ish paragraph
function ContentView({ item, videoRef, watchedPct, setWatchedPct,
                       autoCompleteRef, sendProgress }) {
  const Icon = item.content_type === "video" ? Play :
               item.content_type === "document" ? FileText :
               item.content_type === "link" ? LinkIcon : FileText;

  const url = absoluteUrl(item.content_url);

  // ── Link handler ───────────────────────────────────────────────────
  const handleOpenLink = () => {
    if (url) window.open(url, "_blank");
    sendProgress({
      progress_pct: 50,
      action: "link_opened",
      content_item_id: item.id,
    });
  };

  // ── Video handlers ─────────────────────────────────────────────────
  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    const pct = Math.round((v.currentTime / v.duration) * 100);
    setWatchedPct(pct);
    if (pct >= 90 && !autoCompleteRef.current) {
      autoCompleteRef.current = true;
      sendProgress({
        progress_pct:    100,
        action:          "watch",
        watch_seconds:   Math.round(v.currentTime),
        total_seconds:   Math.round(v.duration),
        content_item_id: item.id,
      });
    }
  };
  const handleVideoEnded = () => {
    if (autoCompleteRef.current) return;
    autoCompleteRef.current = true;
    const v = videoRef.current;
    sendProgress({
      progress_pct:    100,
      action:          "watch",
      watch_seconds:   Math.round(v?.duration || 0),
      total_seconds:   Math.round(v?.duration || 0),
      content_item_id: item.id,
    });
  };

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
        <Icon size={14} color={BLUE} />
        <span style={{ fontSize: 11, color: "#666", textTransform: "uppercase", letterSpacing: 0.5 }}>
          {item.content_type}
        </span>
      </div>

      {/* ── LINK ── */}
      {item.content_type === "link" && (
        <div style={{
          background: "#0f0f0f",
          border: "0.5px solid #1c1c1c",
          borderRadius: 12,
          padding: 32,
          maxWidth: 600,
          margin: "32px auto",
        }}>
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🔗</div>
            <div style={{ fontSize: 16, fontWeight: 500, color: "#fff", marginBottom: 6 }}>
              {item.title}
            </div>
          </div>
          <div style={{
            background: "#0a0a0a", border: "0.5px solid #222", borderRadius: 8,
            padding: "12px 16px", display: "flex", alignItems: "center", gap: 10,
            marginBottom: 20,
          }}>
            <ExternalLink size={13} color="#555" />
            <span style={{
              flex: 1, fontSize: 11, color: "#555",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {item.content_url}
            </span>
            <button
              onClick={handleOpenLink}
              style={{
                background: NEON, color: "#030303", border: "none",
                borderRadius: 6, padding: "6px 14px", fontSize: 12,
                fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
              }}
            >
              Open →
            </button>
          </div>
          <p style={{ fontSize: 13, color: "#555", textAlign: "center", margin: 0 }}>
            This course opens in a new tab. Come back and mark it complete when you are done.
          </p>
        </div>
      )}

      {/* ── VIDEO ── */}
      {item.content_type === "video" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {!isPlaceholderUrl(item.content_url) && url ? (
            <>
              <video
                ref={videoRef}
                src={url}
                controls
                style={{
                  width: "100%", maxWidth: 860,
                  borderRadius: 10, background: "#000",
                  display: "block", aspectRatio: "16/9",
                }}
                onTimeUpdate={handleTimeUpdate}
                onEnded={handleVideoEnded}
              />
              <div style={{ width: "100%", maxWidth: 860 }}>
                <div style={{
                  height: 3, background: "#1a1a1a",
                  borderRadius: 2, overflow: "hidden",
                }}>
                  <div style={{
                    height: "100%", width: `${watchedPct}%`,
                    background: NEON, transition: "width 0.5s", borderRadius: 2,
                  }} />
                </div>
                <div style={{ fontSize: 10, color: "#333", marginTop: 4, textAlign: "right" }}>
                  {watchedPct}% watched
                  {watchedPct >= 90 && " · Completing..."}
                </div>
              </div>
            </>
          ) : (
            <div style={{
              width: "100%", maxWidth: 860, aspectRatio: "16/9",
              background: "#0d0d0d", border: "0.5px solid #1c1c1c", borderRadius: 10,
              display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center", gap: 12, color: "#333",
            }}>
              <div style={{
                width: 60, height: 60, borderRadius: "50%",
                background: "rgba(0,255,153,0.08)", border: "1px solid rgba(0,255,153,0.15)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 24, color: NEON,
              }}>▶</div>
              <div style={{ fontSize: 13, color: "#555" }}>Video content</div>
              {item.content_url && (
                <div style={{ fontSize: 11, color: "#333" }}>{item.content_url}</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── DOCUMENT ── */}
      {item.content_type === "document" && (() => {
        const ext = item.content_url?.split(".").pop()?.toLowerCase();
        const isPdf = ext === "pdf";
        if (isPdf && url) {
          return (
            <div style={{
              width: "100%", height: 580,
              border: "0.5px solid #1c1c1c", borderRadius: 10,
              overflow: "hidden", background: "#0d0d0d",
            }}>
              <iframe
                src={url}
                style={{ width: "100%", height: "100%", border: "none" }}
                title={item.title}
                onLoad={() => sendProgress({
                  progress_pct: 50,
                  action: "read",
                  content_item_id: item.id,
                })}
              />
            </div>
          );
        }
        return (
          <div style={{
            background: "#0f0f0f", border: "0.5px solid #1c1c1c", borderRadius: 12,
            padding: 32, maxWidth: 500, margin: "32px auto", textAlign: "center",
          }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📄</div>
            <div style={{ fontSize: 15, fontWeight: 500, color: "#fff", marginBottom: 8 }}>
              {item.title}
            </div>
            <div style={{ fontSize: 11, color: "#555", marginBottom: 20 }}>
              {item.content_url?.split("/").pop()}
            </div>
            <a
              href={url || "#"}
              target="_blank"
              rel="noreferrer"
              onClick={() => sendProgress({
                progress_pct: 50, action: "read", content_item_id: item.id,
              })}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                background: NEON, color: "#030303",
                padding: "10px 20px", borderRadius: 8,
                fontSize: 13, fontWeight: 600, textDecoration: "none",
              }}
            >
              Download to read →
            </a>
          </div>
        );
      })()}

      {/* ── TEXT ── */}
      {item.content_type === "text" && (
        <p style={{
          fontSize: 14, color: "#bbb", lineHeight: 1.7, whiteSpace: "pre-wrap",
          maxWidth: 760,
        }}>
          {item.content_text || "(no text)"}
        </p>
      )}
    </>
  );
}

function QuizRunner({ course, onResult }) {
  const questions = course.questions || [];
  const [qIdx, setQIdx] = useState(0);
  const [answers, setAnswers] = useState({});  // {question_id: answer}
  const [busy, setBusy] = useState(false);
  const q = questions[qIdx];

  const submit = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/learning/courses/${course.id}/attempt`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          answers: questions.map((qq) => ({ question_id: qq.id, answer: answers[qq.id] || null })),
        }),
      });
      if (!r.ok) throw new Error("Failed");
      onResult(await r.json());
      toast.success("Quiz submitted");
    } catch (e) {
      toast.error("Quiz submission failed");
    } finally {
      setBusy(false);
    }
  };

  if (!q) return <div style={{ color: "#666" }}>This course has no quiz questions.</div>;
  const opts = q.options || [];
  const pct = questions.length ? ((qIdx + 1) / questions.length) * 100 : 0;

  return (
    <div style={{ maxWidth: 680, margin: "0 auto", padding: "8px 0 32px 0" }}>
      {/* Progress indicator */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: "#666", marginBottom: 6 }}>
          Question {qIdx + 1} of {questions.length}
        </div>
        <div style={{ height: 4, background: "#1a1a1a", borderRadius: 2, overflow: "hidden" }}>
          <div style={{ width: `${pct}%`, background: NEON, height: "100%" }} />
        </div>
      </div>

      {/* Question card */}
      <div style={{
        background: "#0f0f0f",
        border: "0.5px solid #1c1c1c",
        borderRadius: 12,
        padding: 24,
        marginBottom: 16,
      }}>
        <h3 style={{
          fontSize: 16, fontWeight: 500, color: "#fff",
          lineHeight: 1.6, marginBottom: 20, marginTop: 0,
        }}>
          {q.question}
        </h3>

        {q.question_type === "mcq" && opts.map((opt, i) => {
          const selected = answers[q.id] === opt.text;
          return (
            <div
              key={i}
              onClick={() => setAnswers({ ...answers, [q.id]: opt.text })}
              style={{
                padding: "12px 16px",
                border: selected ? "1.5px solid rgba(0,255,153,0.4)" : "0.5px solid #1a1a1a",
                borderRadius: 8,
                fontSize: 13,
                color: selected ? NEON : "#888",
                background: selected ? "rgba(0,255,153,0.06)" : "transparent",
                cursor: "pointer",
                marginBottom: 8,
                transition: "all 0.15s",
              }}
            >
              {opt.text}
            </div>
          );
        })}

        {q.question_type === "true_false" && ["true", "false"].map((v) => {
          const selected = answers[q.id] === v;
          return (
            <div
              key={v}
              onClick={() => setAnswers({ ...answers, [q.id]: v })}
              style={{
                padding: "14px 16px",
                border: selected ? "1.5px solid rgba(0,255,153,0.4)" : "0.5px solid #1a1a1a",
                borderRadius: 8,
                fontSize: 14,
                color: selected ? NEON : "#888",
                background: selected ? "rgba(0,255,153,0.06)" : "transparent",
                cursor: "pointer",
                marginBottom: 8,
                textTransform: "capitalize",
                transition: "all 0.15s",
              }}
            >
              {v}
            </div>
          );
        })}
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={() => setQIdx(Math.max(0, qIdx - 1))} disabled={qIdx === 0}
          style={{ ...btnOutline, opacity: qIdx === 0 ? 0.4 : 1 }}>
          ← Previous
        </button>
        <div style={{ flex: 1 }} />
        {qIdx < questions.length - 1 ?
          <button onClick={() => setQIdx(qIdx + 1)} disabled={!answers[q.id]}
            style={{ ...btnPrimary, opacity: !answers[q.id] ? 0.4 : 1 }}>
            Next →
          </button> :
          <button onClick={submit} disabled={busy || !answers[q.id]}
            style={{ ...btnPrimary, opacity: (!answers[q.id] || busy) ? 0.4 : 1 }}>
            {busy ? "Submitting…" : "Submit answers"}
          </button>}
      </div>
    </div>
  );
}

function QuizResult({ course, result, onRetry, onBack }) {
  const color = result.passed ? NEON : RED;
  return (
    <div style={{ maxWidth: 680, margin: "0 auto", padding: "8px 0 32px 0" }}>
      <div style={{
        background: "#0d0d0d",
        border: `0.5px solid ${color}55`,
        borderRadius: 12,
        padding: 22,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 50, height: 50, borderRadius: 25,
            background: `${color}22`, color, display: "flex",
            alignItems: "center", justifyContent: "center",
          }}>
            {result.passed ? <Check size={26} /> : "✗"}
          </div>
          <div>
            <div style={{ fontSize: 24, color, fontWeight: 600 }}>{result.score}%</div>
            <div style={{ fontSize: 12, color: "#888" }}>
              {result.passed ? `Passed (required: ${result.pass_score}%)` : `Failed (required: ${result.pass_score}%)`}
            </div>
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
            Feedback
          </div>
          {(result.feedback || []).map((f, i) => (
            <div key={i} style={{
              padding: "8px 10px", borderRadius: 8, marginBottom: 6,
              background: f.correct ? "rgba(0,255,153,0.05)" : "rgba(226,75,74,0.05)",
              border: `0.5px solid ${f.correct ? NEON : RED}33`,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: f.correct ? NEON : RED }}>
                {f.correct ? <Check size={11} /> : "✗"} Q{i + 1}: Your answer: <em>{String(f.your_answer)}</em>
              </div>
              {!f.correct && (
                <div style={{ fontSize: 10, color: "#888", marginTop: 4 }}>
                  Correct: {String(f.correct_answer)}{f.explanation ? ` · ${f.explanation}` : ""}
                </div>
              )}
              {f.correct && f.explanation && (
                <div style={{ fontSize: 10, color: "#666", marginTop: 4 }}>{f.explanation}</div>
              )}
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button onClick={onBack} style={btnOutline}>← Back to learning</button>
          <div style={{ flex: 1 }} />
          {result.passed && result.certificate_id && (
            <button onClick={async () => {
              try {
                const res = await fetch(
                  `${API_BASE}/api/learning/certificates/${result.certificate_id}/download`,
                  { headers: authHeaders() },
                );
                if (!res.ok) { toast.error("Certificate download failed"); return; }
                const blob = await res.blob();
                const url  = URL.createObjectURL(blob);
                const a    = document.createElement("a");
                a.href     = url;
                a.download = `certificate_${result.certificate_id}.pdf`;
                document.body.appendChild(a); a.click(); document.body.removeChild(a);
                URL.revokeObjectURL(url);
              } catch (e) { toast.error("Certificate download failed"); }
            }} style={btnPrimary}>
              <Award size={12} /> Download certificate
            </button>
          )}
          {!result.passed && result.attempts_remaining > 0 && (
            <button onClick={onRetry} style={btnPrimary}>
              <RotateCcw size={12} /> Retry ({result.attempts_remaining} left)
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ProgressBar({ value }) {
  const v = Math.max(0, Math.min(100, value || 0));
  return (
    <div style={{ height: 5, background: "#1a1a1a", borderRadius: 3, overflow: "hidden" }}>
      <div style={{ width: `${v}%`, background: NEON, height: "100%" }} />
    </div>
  );
}

const btnPrimary = {
  display: "inline-flex", alignItems: "center", gap: 6,
  padding: "8px 16px", fontSize: 12, borderRadius: 8,
  background: `${NEON}1f`, border: `0.5px solid ${NEON}55`,
  color: NEON, cursor: "pointer", fontWeight: 500,
};
const btnOutline = {
  display: "inline-flex", alignItems: "center", gap: 6,
  padding: "8px 14px", fontSize: 12, borderRadius: 8,
  background: "transparent", border: "0.5px solid #2a2a2a",
  color: "#bbb", cursor: "pointer",
};
